import React, { type ReactElement, useState } from 'react';
import { Box } from '@saviynt/design-system';
import cx from 'classnames';

import { ALIGNMENT_PROPERTY_ORIENTATION } from '../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../shared/hooks';
import { type AlignmentTypes } from '../../../../../shared/types';
import { ensureDataUrl } from '../../../../../shared/utils';
import { brandingMessages } from '../messages';

import './BrandingLogo.css';

interface BrandingLogoProps {
  logo?: string;
  logoAlignment?: AlignmentTypes | null;
  classNames?: {
    logoImg?: string;
  };
}

function BrandingLogo({
  logo,
  logoAlignment = ALIGNMENT_PROPERTY_ORIENTATION.CENTER,
  classNames,
}: BrandingLogoProps): ReactElement | null {
  const l = useInvertedLocalize(brandingMessages);
  const logoSrc = ensureDataUrl(logo);

  // Track whether the image failed to load (covers 404, 400, or any non-2xx response).
  // The form layout is not blocked — this component renders independently and the
  // error state is handled locally without propagating to parent components.
  const [hasError, setHasError] = useState(false);

  if (!logoSrc || hasError) {
    return null;
  }

  return (
    <Box
      className={cx(
        'FF-BrandingLogo',
        `FF-BrandingLogo--${logoAlignment ?? ALIGNMENT_PROPERTY_ORIENTATION.CENTER}`
      )}>
      <img
        src={logoSrc}
        alt={l('Logo')}
        className={cx('FF-BrandingLogo-img', classNames?.logoImg)}
        // Clear the logo on any load failure (404, 400, or other non-2xx) so the
        // form continues to render without a broken image or unhandled error.
        onError={() => setHasError(true)}
      />
    </Box>
  );
}

export default BrandingLogo;
